/**
 * reCAPTCHA server-side validation utility
 */

export interface RecaptchaVerifyResponse {
  success: boolean;
  score: number;
  action: string;
  challenge_ts: string;
  hostname: string;
  "error-codes"?: string[];
}

/**
 * Verify reCAPTCHA token on the server side
 * @param token - The reCAPTCHA response token from the client
 * @param secretKey - The reCAPTCHA secret key
 * @returns Promise<RecaptchaVerifyResponse>
 */
export async function verifyRecaptchaToken(
  token: string,
  secretKey: string,
): Promise<RecaptchaVerifyResponse> {
  const response = await fetch(
    "https://www.google.com/recaptcha/api/siteverify",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: `secret=${encodeURIComponent(secretKey)}&response=${encodeURIComponent(token)}`,
    },
  );

  if (!response.ok) {
    throw new Error(`reCAPTCHA verification failed: ${response.statusText}`);
  }

  const data = await response.json();
  return data as RecaptchaVerifyResponse;
}

/**
 * Check if reCAPTCHA verification was successful and meets score threshold
 * @param response - The reCAPTCHA verification response
 * @param expectedAction - The expected action name
 * @param minScore - Minimum score threshold (default: 0.5)
 * @returns boolean
 */
export function isRecaptchaValid(
  response: RecaptchaVerifyResponse,
  expectedAction: string,
  minScore = 0.5,
): boolean {
  return (
    response.success &&
    response.action === expectedAction &&
    response.score >= minScore
  );
}

/**
 * Get reCAPTCHA validation result with detailed information
 * @param response - The reCAPTCHA verification response
 * @param expectedAction - The expected action name
 * @param minScore - Minimum score threshold (default: 0.5)
 * @returns object with validation result and details
 */
export function getRecaptchaValidationResult(
  response: RecaptchaVerifyResponse,
  expectedAction: string,
  minScore = 0.5,
) {
  const isValid = isRecaptchaValid(response, expectedAction, minScore);

  return {
    isValid,
    score: response.score,
    action: response.action,
    expectedAction,
    minScore,
    success: response.success,
    errors: response["error-codes"] || [],
    hostname: response.hostname,
    timestamp: response.challenge_ts,
  };
}

/**
 * Server-side reCAPTCHA validation with action support
 * @param recaptchaToken - The reCAPTCHA response token from the client
 * @param expectedAction - The expected action name (default: "email_lead_capture")
 * @param minValidScore - Minimum score threshold (default: 0.5)
 * @returns Promise<{recaptchaScore: number, recaptchaValid: boolean}>
 */
export async function checkRecaptcha(
  recaptchaToken: string,
  expectedAction = "email_lead_capture",
  minValidScore = 0.5,
) {
  try {
    const secretKey = process.env["RECAPTCHA_SECRET_KEY"];

    if (!secretKey) {
      console.warn("reCAPTCHA secret key not found");
      // return true if we can't verify the recaptcha token
      return { recaptchaScore: 0, recaptchaValid: true };
    }

    const response = await verifyRecaptchaToken(recaptchaToken, secretKey);
    const validation = getRecaptchaValidationResult(
      response,
      expectedAction,
      minValidScore,
    );

    const recaptchaScore = validation.score;
    const recaptchaValid = validation.isValid;

    // Log for analytics and monitoring, per Google's recommendations
    console.log("reCAPTCHA validation result:", {
      success: validation.success,
      isValid: validation.isValid,
      score: validation.score,
      action: validation.action,
      expectedAction: validation.expectedAction,
      hostname: validation.hostname,
      timestamp: validation.timestamp,
    });

    return { recaptchaScore, recaptchaValid };
  } catch (error) {
    console.error("reCAPTCHA verification error:", error);
    // return true if we can't verify the recaptcha token
    return { recaptchaScore: 0, recaptchaValid: true };
  }
}
